import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { DashboardFilterBar } from '@/features/dashboard-filters';

import { useMappingDashboard } from '../lib/use-mapping-dashboard';
import { useMappingFilters } from '../lib/use-mapping-filters';
import { PageHeader, MapControls, MapContainer, StatusInfo } from './components';

export function MappingPage() {
  const { t } = useTranslation();
  const filters = useMappingFilters();

  const dashboard = useMappingDashboard({
    organizationId: filters.selectedOrganization
  });

  // Error state
  if (dashboard.error) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <p className="text-destructive">Error loading mapping data</p>
        </Card>
      </div>
    );
  }

  // Loading state
  if (dashboard.isLoading && dashboard.locations.length === 0) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500">Loading locations...</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader title={t('welcome.mapping.label', 'Mapping')} />

      {/* Filters Section */}
      <DashboardFilterBar
        variant="mapping"
        options={{}}
        onOptionChange={() => {}}
        consignmentNumber={filters.consignmentNumber}
        onConsignmentNumberChange={filters.setConsignmentNumber}
        organizationOptions={filters.organizationOptions}
        selectedOrganization={filters.selectedOrganization}
        onOrganizationChange={filters.setSelectedOrganization}
        onOrganizationSearchChange={filters.setOrganizationSearch}
        isLoadingOrganizations={filters.isLoadingOrganizations}
        vehicleCategoryOptions={filters.vehicleCategoryOptions}
        selectedVehicleCategories={filters.selectedVehicleCategories}
        onVehicleCategoriesChange={filters.setSelectedVehicleCategories}
        showVehicleCategories={dashboard.corridorType === 'RIT'}
      />

      {/* Map Section with Selected Location Panel */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Map Container */}
        <div className="flex-1">
          <Card className="p-4 space-y-4">
            {/* Map Controls */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <MapControls
                // Location filter
                locationTypeFilter={dashboard.locationTypeFilter}
                locationsCount={dashboard.locations.length}
                filteredCount={
                  dashboard.locations.filter(
                    (l) => dashboard.locationTypeFilter[l.type as keyof typeof dashboard.locationTypeFilter]
                  ).length
                }
                onLocationTypeToggle={dashboard.handleLocationTypeToggle}
                onToggleAllLocationTypes={dashboard.toggleAllLocationTypes}
                // Corridors
                showCorridors={dashboard.showCorridors}
                corridorType={dashboard.corridorType}
                onToggleCorridors={() => dashboard.setShowCorridors(!dashboard.showCorridors)}
                onCorridorTypeChange={dashboard.setCorridorType}
                // Time machine
                showTimeMachine={dashboard.showTimeMachine}
                onToggleTimeMachine={() => dashboard.setShowTimeMachine(!dashboard.showTimeMachine)}
              />

              {/* Status Info */}
              <StatusInfo
                filteredLocationCounts={dashboard.filteredLocationCounts}
                routeCount={dashboard.corridors.length}
                showRoutes={dashboard.showCorridors}
              />
            </div>

            {/* Map */}
            <MapContainer
              locations={dashboard.locations}
              corridors={dashboard.corridors}
              selectedLocation={dashboard.selectedLocation}
              showCorridors={dashboard.showCorridors}
              showTimeMachine={dashboard.timeMachine.isActive}
              dataLayer={dashboard.dataLayer}
              timeMachine={dashboard.timeMachine}
              isLoading={dashboard.isDataFetching}
              loadingMessage={
                dashboard.timeMachine.weekNo
                  ? `Loading ${dashboard.dataLayer} data for week ${dashboard.timeMachine.weekNo}...`
                  : `Loading ${dashboard.dataLayer} data...`
              }
              onLocationSelect={dashboard.setSelectedLocation}
              onDataLayerChange={dashboard.setDataLayer}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}
